import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Form,
  FormField,
  Input,
  Button,
  Box,
  Badge,
  Alert,
  ColumnLayout
} from '@cloudscape-design/components';
import { useRole } from '../contexts/RoleContext';
import { fetchAuthSession } from 'aws-amplify/auth';
import apiService from '../services/api';

interface UserProfile {
  email: string;
  givenName: string;
  familyName: string;
  phoneNumber: string;
  role: string;
  createdAt: string;
}

export const UserProfile: React.FC = () => {
  const { role } = useRole();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Form state
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isOAuthUser, setIsOAuthUser] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (idToken) {
        const payload = idToken.payload as any;
        
        const profileData: UserProfile = {
          email: payload.email || '',
          givenName: payload.given_name || '',
          familyName: payload.family_name || '',
          phoneNumber: payload.phone_number || '',
          role: payload['custom:role'] || 'User',
          createdAt: new Date(payload.auth_time * 1000).toLocaleDateString()
        };
        
        setProfile(profileData);
        setGivenName(profileData.givenName);
        setFamilyName(profileData.familyName);
        setPhoneNumber(profileData.phoneNumber);
        
        // Check if OAuth user (Google, Facebook, etc.)
        const identities = payload.identities;
        setIsOAuthUser(!!identities);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setErrorMessage('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setErrorMessage('');
      setSuccessMessage('');
      
      // Call API to update profile
      const response = await apiService.put('/api/user/profile', {
        given_name: givenName,
        family_name: familyName,
        phone_number: phoneNumber
      });
      
      if ((response.data as any)?.status === 'error') {
        setErrorMessage((response.data as any).error || 'Failed to update profile');
        return;
      }
      
      // Update local profile state immediately (JWT won't reflect changes until refresh)
      setProfile(prev => prev ? {
        ...prev,
        givenName,
        familyName,
        phoneNumber
      } : null);
      
      setSuccessMessage('Profile updated successfully');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setErrorMessage('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setErrorMessage('New passwords do not match');
      return;
    }
    
    // Validate password complexity
    if (newPassword.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return;
    }
    
    if (!/[A-Z]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one uppercase letter');
      return;
    }
    
    if (!/[a-z]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one lowercase letter');
      return;
    }
    
    if (!/[0-9]/.test(newPassword)) {
      setErrorMessage('Password must contain at least one number');
      return;
    }
    
    try {
      setChangingPassword(true);
      setErrorMessage('');
      setSuccessMessage('');
      
      // Call API to change password
      const response = await apiService.post('/api/user/profile/password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      if ((response.data as any)?.status === 'error') {
        setErrorMessage((response.data as any).error || 'Failed to change password');
        return;
      }
      
      setSuccessMessage('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error changing password:', error);
      setErrorMessage('Failed to change password. Please check your current password.');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding="xxl">
          Loading profile...
        </Box>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container>
        <Alert type="error">Failed to load profile</Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1">User Profile</Header>
      
      {successMessage && (
        <Alert type="success" dismissible onDismiss={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert type="error" dismissible onDismiss={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}
      
      <Container header={<Header variant="h2">Profile Information</Header>}>
        <form onSubmit={handleUpdateProfile}>
          <Form
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button formAction="none" variant="link" onClick={loadProfile}>
                  Cancel
                </Button>
                <Button variant="primary" loading={saving}>
                  Save Changes
                </Button>
              </SpaceBetween>
            }
          >
            <SpaceBetween size="l">
              <ColumnLayout columns={2}>
                <FormField label="Email" description="Email cannot be changed">
                  <Input value={profile.email} disabled />
                </FormField>
                
                <FormField label="Role" description="Contact an administrator to change your role">
                  <Box>
                    <Badge color={role === 'Admin' ? 'red' : role === 'Production' ? 'blue' : 'grey'}>
                      {profile.role}
                    </Badge>
                  </Box>
                </FormField>
              </ColumnLayout>
              
              <ColumnLayout columns={2}>
                <FormField label="First Name">
                  <Input
                    value={givenName}
                    onChange={({ detail }) => setGivenName(detail.value)}
                  />
                </FormField>
                
                <FormField label="Last Name">
                  <Input
                    value={familyName}
                    onChange={({ detail }) => setFamilyName(detail.value)}
                  />
                </FormField>
              </ColumnLayout>
              
              <FormField label="Phone Number" description="Format: +1234567890">
                <Input
                  value={phoneNumber}
                  onChange={({ detail }) => setPhoneNumber(detail.value)}
                  placeholder="+1234567890"
                />
              </FormField>
              
              <FormField label="Account Created">
                <Input value={profile.createdAt} disabled />
              </FormField>
            </SpaceBetween>
          </Form>
        </form>
      </Container>
      
      {!isOAuthUser && (
        <Container header={<Header variant="h2">Change Password</Header>}>
          <form onSubmit={handleChangePassword}>
            <Form
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button 
                    formAction="none" 
                    variant="link" 
                    onClick={() => {
                      setCurrentPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                    }}
                  >
                    Clear
                  </Button>
                  <Button variant="primary" loading={changingPassword}>
                    Change Password
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="l">
                <Alert type="info">
                  Password must be at least 8 characters long and contain uppercase, lowercase, and numbers.
                </Alert>
                
                <FormField label="Current Password">
                  <Input
                    type="password"
                    value={currentPassword}
                    onChange={({ detail }) => setCurrentPassword(detail.value)}
                  />
                </FormField>
                
                <FormField label="New Password">
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={({ detail }) => setNewPassword(detail.value)}
                  />
                </FormField>
                
                <FormField label="Confirm New Password">
                  <Input
                    type="password"
                    value={confirmPassword}
                    onChange={({ detail }) => setConfirmPassword(detail.value)}
                  />
                </FormField>
              </SpaceBetween>
            </Form>
          </form>
        </Container>
      )}
      
      {isOAuthUser && (
        <Container>
          <Alert type="info">
            You are signed in with a social provider (Google, Facebook, etc.). 
            Password changes are not available for social sign-in accounts.
          </Alert>
        </Container>
      )}
    </SpaceBetween>
  );
};

export default UserProfile;
